import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Phone, MapPin, MessageCircle, ExternalLink, Send, CheckCircle2 } from "lucide-react";

const WA_URL = "https://wa.me/51932049397?text=" + encodeURIComponent(
  "Hola, me gustaría información sobre los servicios de CIND y agendar una cita."
);
const FB_URL = "https://www.facebook.com/profile.php?id=61568596892252";

const contactInfo = [
  { icon: Phone,   label: "Teléfonos",  value: "+51 932 049 397  /  +51 984 246 723",  href: "tel:+51932049397" },
  { icon: MapPin,  label: "Dirección",  value: "Pasaje Felisa Moscoso 102, Arequipa",  href: null },
];

const motivos = [
  "Terapia de Lenguaje",
  "Terapia de Conducta",
  "Terapia Cognitivo Conductual",
  "Terapia Ocupacional",
  "Evaluación de Lenguaje",
  "Evaluación del Neurodesarrollo",
  "Evaluación de Autismo (TEA)",
  "Evaluación de TDAH",
  "Otro",
];

export default function Contact() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const [form, setForm] = useState({ nombre: "", telefono: "", motivo: "" });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.nombre.trim())   e.nombre   = "Por favor ingresa tu nombre.";
    if (!form.telefono.trim()) e.telefono = "Por favor ingresa tu teléfono.";
    if (!form.motivo)          e.motivo   = "Selecciona el motivo de consulta.";
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      const firstKey = Object.keys(errs)[0];
      document.getElementById(`field-${firstKey}`)?.focus();
      return;
    }
    setLoading(true);
    const msg = encodeURIComponent(
      `Hola, soy ${form.nombre}. Mi número es ${form.telefono}. Motivo: ${form.motivo}.`
    );
    setTimeout(() => {
      setLoading(false);
      setSent(true);
      window.open(`https://wa.me/51932049397?text=${msg}`, "_blank", "noopener,noreferrer");
    }, 600);
  };

  return (
    <section
      id="contacto"
      ref={ref}
      className="py-24 bg-white"
      aria-labelledby="contact-heading"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">

        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-14"
        >
          <p className="font-sans text-xs font-semibold tracking-widest text-primary uppercase mb-3">
            Contacto
          </p>
          <h2 id="contact-heading" className="section-heading text-3xl sm:text-4xl mb-4">
            ¿Listo para dar el primer paso?
          </h2>
          <div className="w-12 h-0.5 bg-primary rounded-full" aria-hidden="true" />
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">

          {/* Left column — info + WhatsApp */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <p className="font-sans text-base text-txtMuted leading-relaxed mb-8 max-w-md">
              Estamos aquí para orientarte. Escríbenos por WhatsApp o completa el formulario
              y nos pondremos en contacto contigo a la brevedad.
            </p>

            {/* WhatsApp CTA */}
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-sans font-semibold text-base px-7 py-4 rounded-xl transition-colors duration-150 shadow-sm hover:shadow-md mb-8 w-full sm:w-auto justify-center sm:justify-start"
              style={{ minHeight: "52px" }}
              aria-label="Contactar por WhatsApp"
            >
              <MessageCircle size={20} aria-hidden="true" />
              Escribir por WhatsApp
            </a>

            {/* Contact info */}
            <ul className="flex flex-col gap-4 mb-8">
              {contactInfo.map(({ icon: Icon, label, value, href }) => (
                <li key={label} className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0"
                    aria-hidden="true"
                  >
                    <Icon size={18} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-sans text-xs font-semibold text-txtMuted uppercase tracking-wide mb-0.5">{label}</p>
                    {href ? (
                      <a href={href} className="font-sans text-sm text-txt hover:text-primary transition-colors">
                        {value}
                      </a>
                    ) : (
                      <p className="font-sans text-sm text-txt">{value}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {/* Facebook link */}
            <a
              href={FB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-sans text-sm font-medium text-primary hover:text-primaryDk transition-colors"
              aria-label="Visitar Facebook de CIND (abre en nueva pestaña)"
            >
              <span
                className="w-8 h-8 rounded-lg bg-[#1877F2] flex items-center justify-center"
                aria-hidden="true"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                </svg>
              </span>
              Seguirnos en Facebook
              <ExternalLink size={13} aria-hidden="true" />
            </a>
          </motion.div>

          {/* Right column — form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {sent ? (
              <div
                role="status"
                aria-live="polite"
                className="flex flex-col items-center justify-center gap-4 py-16 text-center"
              >
                <CheckCircle2 size={48} className="text-sage" aria-hidden="true" />
                <h3 className="font-serif font-bold text-xl text-navy">
                  ¡Mensaje enviado correctamente!
                </h3>
                <p className="font-sans text-sm text-txtMuted max-w-xs">
                  Te hemos redirigido a WhatsApp. Nos pondremos en contacto contigo pronto.
                </p>
                <button
                  onClick={() => { setSent(false); setForm({ nombre: "", telefono: "", motivo: "" }); }}
                  className="btn-outline text-sm px-6 py-2.5 mt-2"
                  style={{ minHeight: "40px" }}
                >
                  Enviar otro mensaje
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                noValidate
                aria-label="Formulario de contacto"
                className="flex flex-col gap-5"
              >
                {/* Nombre */}
                <div>
                  <label
                    htmlFor="field-nombre"
                    className="block font-sans text-sm font-medium text-txt mb-1.5"
                  >
                    Nombre completo <span className="text-[#c0392b]" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="field-nombre"
                    name="nombre"
                    type="text"
                    value={form.nombre}
                    onChange={handleChange}
                    autoComplete="name"
                    className="input-field"
                    aria-required="true"
                    aria-describedby={errors.nombre ? "err-nombre" : undefined}
                    aria-invalid={!!errors.nombre}
                    placeholder="Ej. María García"
                  />
                  {errors.nombre && (
                    <p id="err-nombre" role="alert" className="font-sans text-xs text-[#c0392b] mt-1.5">
                      {errors.nombre}
                    </p>
                  )}
                </div>

                {/* Teléfono */}
                <div>
                  <label
                    htmlFor="field-telefono"
                    className="block font-sans text-sm font-medium text-txt mb-1.5"
                  >
                    Teléfono / WhatsApp <span className="text-[#c0392b]" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="field-telefono"
                    name="telefono"
                    type="tel"
                    value={form.telefono}
                    onChange={handleChange}
                    autoComplete="tel"
                    className="input-field"
                    aria-required="true"
                    aria-describedby={errors.telefono ? "err-telefono" : undefined}
                    aria-invalid={!!errors.telefono}
                    placeholder="Ej. 987 654 321"
                  />
                  {errors.telefono && (
                    <p id="err-telefono" role="alert" className="font-sans text-xs text-[#c0392b] mt-1.5">
                      {errors.telefono}
                    </p>
                  )}
                </div>

                {/* Motivo */}
                <div>
                  <label
                    htmlFor="field-motivo"
                    className="block font-sans text-sm font-medium text-txt mb-1.5"
                  >
                    Motivo de consulta <span className="text-[#c0392b]" aria-hidden="true">*</span>
                  </label>
                  <select
                    id="field-motivo"
                    name="motivo"
                    value={form.motivo}
                    onChange={handleChange}
                    className="input-field cursor-pointer"
                    aria-required="true"
                    aria-describedby={errors.motivo ? "err-motivo" : undefined}
                    aria-invalid={!!errors.motivo}
                  >
                    <option value="">Seleccionar motivo…</option>
                    {motivos.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  {errors.motivo && (
                    <p id="err-motivo" role="alert" className="font-sans text-xs text-[#c0392b] mt-1.5">
                      {errors.motivo}
                    </p>
                  )}
                </div>

                <p className="font-sans text-xs text-txtMuted">
                  <span className="text-[#c0392b]" aria-hidden="true">*</span> Campos requeridos
                </p>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full text-base py-3.5 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" aria-hidden="true" />
                      Enviando…
                    </>
                  ) : (
                    <>
                      <Send size={17} aria-hidden="true" />
                      Enviar consulta
                    </>
                  )}
                </button>

                <p className="font-sans text-xs text-txtMuted text-center leading-relaxed">
                  Al enviar, serás redirigido a WhatsApp para continuar la conversación.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
